/**
 * CRM Fields Adapter
 *
 * Converts CRM fields from API to FieldPickerPopover format.
 * Supports multi-source mode with sidebar (Deal, Lead, Contact, Company).
 */

import { FileText, Target, User, Building2 } from 'lucide-react';
import { createElement } from 'react';
import i18n from '@/i18n/config';
import type { PickerField, PickerFieldGroup, PickerFieldSource } from '../types';

/**
 * CRM field type (after mapping from Bitrix24)
 */
export type CrmFieldType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'enum'
  | 'date'
  | 'datetime'
  | 'money'
  | 'user'
  | 'file';

/**
 * CRM field from API
 */
export interface CrmField {
  fieldId: string;
  title: string;
  entityType?: 'deal' | 'lead' | 'contact' | 'company';
  /** Field type (mapped: integer→number, enumeration→enum) */
  type?: CrmFieldType | string;
}

/**
 * Source IDs for CRM entity types
 */
export const CRM_SOURCE_IDS = {
  deal: 'crm-deal',
  lead: 'crm-lead',
  contact: 'crm-contact',
  company: 'crm-company',
} as const;

/**
 * Source configurations with icons and labels
 */
const CRM_SOURCE_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; order: number }
> = {
  [CRM_SOURCE_IDS.deal]: {
    label: 'Deal',
    icon: createElement(FileText, { className: 'h-4 w-4' }),
    order: 1,
  },
  [CRM_SOURCE_IDS.lead]: {
    label: 'Lead',
    icon: createElement(Target, { className: 'h-4 w-4' }),
    order: 2,
  },
  [CRM_SOURCE_IDS.contact]: {
    label: 'Contact',
    icon: createElement(User, { className: 'h-4 w-4' }),
    order: 3,
  },
  [CRM_SOURCE_IDS.company]: {
    label: 'Company',
    icon: createElement(Building2, { className: 'h-4 w-4' }),
    order: 4,
  },
};

/**
 * Map entity type to source ID
 */
function entityTypeToSourceId(entityType?: string): string {
  switch (entityType) {
    case 'deal':
      return CRM_SOURCE_IDS.deal;
    case 'lead':
      return CRM_SOURCE_IDS.lead;
    case 'contact':
      return CRM_SOURCE_IDS.contact;
    case 'company':
      return CRM_SOURCE_IDS.company;
    default:
      return CRM_SOURCE_IDS.deal;
  }
}

/**
 * Map CRM field type to picker inputType
 */
function mapCrmTypeToInputType(crmType?: string): PickerField['inputType'] {
  switch (crmType) {
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'enum':
      return 'select';
    case 'date':
    case 'datetime':
      return 'date';
    case 'string':
    default:
      return 'text';
  }
}

/**
 * Convert a single CRM field to PickerField
 */
function crmFieldToPickerField(field: CrmField): PickerField {
  return {
    name: field.fieldId,
    label: field.title,
    group: 'all', // Will be grouped by source
    inputType: mapCrmTypeToInputType(field.type),
    description: field.type ? `${field.fieldId} (${field.type})` : field.fieldId, // Show field code and type
    disabled: false,
  };
}

/**
 * Convert CRM fields to FieldPickerPopover simple format (fields + groups)
 * Use this for simpler UI without source sidebar
 */
export function crmFieldsToPickerFormat(
  crmFields: CrmField[],
  entityTypeLabels?: Record<string, string>
): {
  fields: PickerField[];
  groups: PickerFieldGroup[];
} {
  const fields: PickerField[] = crmFields.map((field) => ({
    ...crmFieldToPickerField(field),
    group: field.entityType || 'other',
  }));

  // Extract unique entity types
  const uniqueEntityTypes = new Set<string>();
  crmFields.forEach((field) => {
    if (field.entityType) {
      uniqueEntityTypes.add(field.entityType);
    }
  });

  const groups: PickerFieldGroup[] = Array.from(uniqueEntityTypes).map((entityType) => {
    const sourceId = entityTypeToSourceId(entityType);
    const config = CRM_SOURCE_CONFIG[sourceId];
    return {
      key: entityType,
      label: entityTypeLabels?.[entityType] || config?.label || entityType,
      icon: config?.icon,
      order: config?.order || 99,
      defaultCollapsed: false,
    };
  });

  // Sort groups by order
  groups.sort((a, b) => (a.order || 0) - (b.order || 0));

  return { fields, groups };
}

/**
 * Convert CRM fields to FieldPickerPopover multi-source format
 * Use this for full UI with source sidebar
 */
export function crmFieldsToSourcesFormat(
  crmFields: CrmField[],
  entityTypeLabels?: Record<string, string>
): PickerFieldSource[] {
  // Group fields by entity type
  const fieldsByEntity = new Map<string, PickerField[]>();

  for (const field of crmFields) {
    const entityType = field.entityType || 'deal';
    const sourceId = entityTypeToSourceId(entityType);
    const pickerField = crmFieldToPickerField(field);

    if (!fieldsByEntity.has(sourceId)) {
      fieldsByEntity.set(sourceId, []);
    }
    fieldsByEntity.get(sourceId)!.push(pickerField);
  }

  // Build sources array
  const sources: PickerFieldSource[] = [];

  for (const [sourceId, fields] of fieldsByEntity) {
    const config = CRM_SOURCE_CONFIG[sourceId];
    if (!config) continue;

    // For each source, create a single "All Fields" group
    const groups: PickerFieldGroup[] = [
      {
        key: 'all',
        label: i18n.t('settings:routingFieldPicker.groups.allFields'),
        order: 1,
        defaultCollapsed: false,
      },
    ];

    // Get entity type from source ID
    const entityType = sourceId.replace('crm-', '') as keyof typeof CRM_SOURCE_IDS;

    sources.push({
      id: sourceId,
      label: entityTypeLabels?.[entityType] || config.label,
      icon: config.icon,
      groups,
      fields,
    });
  }

  // Sort by order
  sources.sort((a, b) => {
    const orderA = CRM_SOURCE_CONFIG[a.id]?.order || 99;
    const orderB = CRM_SOURCE_CONFIG[b.id]?.order || 99;
    return orderA - orderB;
  });

  return sources;
}

/**
 * Helper: Check if CRM fields have multiple entity types
 * If only one entity type, use simple mode without sidebar
 */
export function hasMultipleEntityTypes(crmFields: CrmField[]): boolean {
  const entityTypes = new Set(crmFields.map((f) => f.entityType).filter(Boolean));
  return entityTypes.size > 1;
}

/**
 * Helper: Count fields by entity type
 */
export function countFieldsByEntityType(crmFields: CrmField[]): Record<string, number> {
  const counts: Record<string, number> = {};

  for (const field of crmFields) {
    const entityType = field.entityType || 'other';
    counts[entityType] = (counts[entityType] || 0) + 1;
  }

  return counts;
}

/**
 * Filter CRM fields by expected types
 *
 * @param crmFields - All CRM fields
 * @param expectedTypes - Array of expected field types (e.g., ['number'] for numeric params)
 * @returns Filtered fields matching the expected types
 */
export function filterFieldsByType(
  crmFields: CrmField[],
  expectedTypes?: CrmFieldType[]
): CrmField[] {
  if (!expectedTypes || expectedTypes.length === 0) {
    return crmFields;
  }

  return crmFields.filter((field) => {
    const fieldType = field.type as CrmFieldType | undefined;
    if (!fieldType) return true; // Include fields without type (backward compatibility)
    return expectedTypes.includes(fieldType);
  });
}

/**
 * Convert CRM fields to FieldPickerPopover sources format with type filtering
 *
 * @param crmFields - All CRM fields
 * @param entityTypeLabels - Localized labels for entity types
 * @param expectedTypes - Array of expected field types for filtering
 */
export function crmFieldsToSourcesFormatFiltered(
  crmFields: CrmField[],
  entityTypeLabels?: Record<string, string>,
  expectedTypes?: CrmFieldType[]
): PickerFieldSource[] {
  const filteredFields = filterFieldsByType(crmFields, expectedTypes);
  return crmFieldsToSourcesFormat(filteredFields, entityTypeLabels);
}
